import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import Joi from 'joi';

import { useCognitoSignInMutation, useSignInMutation } from 'api/main';
import { validateSchema } from 'utils/helpers';
import { useSmallScreen } from 'utils/hooks';
import { EMAIL_VALIDATION_OPTIONS, userChallenges } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import ConfirmSignInDialog from './ConfirmSignInDialog';
import { CustomTextField, ErrorMessage, SubmitButton, PasswordField } from 'components';
import TunnelBox from 'containers/tunnel/common/TunnelBox';
import TunnelStep from 'containers/tunnel/common/TunnelStep';
import { Box, Typography, Link } from '@mui/material';

const schema = {
    email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
        'string.empty': texts.SAISIR_VOTRE_ADRESSE_EMAIL,
        'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
    }),
    password: Joi.string().required().messages({ 'string.empty': texts.PASSWORD_REQUIRED }),
};

const SignIn = () => {
    const isSmall = useSmallScreen();
    const history = useHistory();

    const isEmailConfirmed = useRef(!!history?.location?.state?.isEmailConfirmed);
    const isRoleDistributor = useRef(!!history?.location?.state?.isRoleDistributor);

    const [signIn, { isLoading: buttonLoader }] = useSignInMutation();

    const [
        cognitoSignIn,
        {
            isLoading: cognitoLoader,
            isError: loginFail,
            data: cognitoResponse,
            isSuccess: cognitoSuccess,
            error,
            reset,
        },
    ] = useCognitoSignInMutation({ fixedCacheKey: 'cognito-sign-in' });

    const [form, setForm] = useState({
        email: '',
        password: '',
    });
    const [errors, setErrors] = useState({
        email: '',
        password: '',
    });
    const [confirmSignInOpen, setConfirmSignInOpen] = useState(false);

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        return valid;
    }, [form]);

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(form => ({ ...form, [key]: key === 'email' ? value.trim() : value }));
        },
        []
    );

    const handleBlur = useCallback(
        key => e => {
            const { value } = e.target;

            let { error } = schema[key].validate(value);
            setErrors(errors => ({ ...errors, [key]: error ? error.details[0].message : '' }));
        },
        []
    );

    const handleSubmit = useCallback(
        e => {
            e.preventDefault();

            if (formIsValid()) {
                cognitoSignIn(form);
            }
        },
        [formIsValid, cognitoSignIn, form]
    );

    useEffect(() => {
        if (loginFail) {
            setTimeout(() => {
                reset();
            }, 10000);
        }
    }, [loginFail, reset]);

    useEffect(() => {
        if (cognitoSuccess) {
            if (cognitoResponse?.nextStep?.signInStep === userChallenges.SMS_CODE_REQUIRED) {
                // if user is serveicer - trigger 2FA flow
                setConfirmSignInOpen(true);
            } else if (!cognitoResponse?.isSignedIn) {
                signIn();
            }
            reset();
        }
    }, [cognitoSuccess, cognitoResponse, signIn, reset]);

    return (
        <Box
            sx={theme => ({
                paddingTop: `${theme.constants.appBarHeight}px`,
                width: '100%',
            })}
        >
            <TunnelStep title={texts.CONNEXION}>
                {isEmailConfirmed.current && (
                    <Typography align="center" sx={{ mb: 2 }}>
                        {texts.VOTRE_EMAIL_A_ETE_VALIDE}{' '}
                        {isRoleDistributor.current
                            ? texts.POUVOIR_CONTINUER_LE_TUNNEL_DISTRIBUTOR
                            : texts.POUVOIR_CONTINUER_LE_TUNNEL}
                    </Typography>
                )}
                <ConfirmSignInDialog
                    open={confirmSignInOpen}
                    onClose={() => setConfirmSignInOpen(false)}
                    onSuccess={signIn}
                />
                <TunnelBox>
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={isSmall ? {} : { display: 'flex', justifyContent: 'center' }}
                    >
                        <Box sx={{ flexBasis: isSmall ? 'unset' : '50%' }}>
                            <CustomTextField
                                inputProps={{
                                    autoCapitalize: 'off',
                                    autoComplete: 'off',
                                    spellCheck: 'off',
                                    autoCorrect: 'off',
                                }}
                                sx={theme => ({
                                    maxWidth: `${theme.constants.tunnelInputWidth}px`,
                                })}
                                label={texts.IDENTIFIANT}
                                value={form.email}
                                onChange={handleChange('email')}
                                onBlur={handleBlur('email')}
                                error={errors.email}
                                placeholder="exemple@email.com"
                            />
                            <PasswordField
                                sx={theme => ({
                                    maxWidth: `${theme.constants.tunnelInputWidth}px`,
                                })}
                                label={texts.MOT_DE_PASSE}
                                value={form.password}
                                onChange={handleChange('password')}
                                onBlur={handleBlur('password')}
                                error={errors.password}
                            />
                            <button type="submit" hidden />
                            {!!error && error !== 403 && (
                                <ErrorMessage message={texts.IDENTIFIANTS_INVALIDES} />
                            )}
                            {error === 403 && (
                                <ErrorMessage message={texts.VOTRE_COMPTE_N_EST_PAS_VALIDE} />
                            )}
                        </Box>
                    </Box>
                </TunnelBox>
                <Box sx={{ width: 'fit-content', ml: 'auto', mb: '15px', fontSize: '13px' }}>
                    <Link
                        sx={{ textDecoration: 'none', color: 'primary.main' }}
                        to="/forgot-password"
                        href="/forgot-password"
                        component={RouterLink}
                    >
                        {texts.MOT_DE_PASSE_OUBLIE}
                    </Link>
                </Box>
                <SubmitButton
                    text={texts.SE_CONNECTER}
                    onClick={handleSubmit}
                    disabled={buttonLoader || cognitoLoader}
                    loading={buttonLoader || cognitoLoader}
                />
            </TunnelStep>
        </Box>
    );
};

export default SignIn;
